import {
  DocCode,
  DocEm,
  DocH2,
  DocH3,
  DocHr,
  DocLink,
  DocP,
  DocTable,
  DocUl,
} from "@/components/ec/documentSection";

export function PrivacyPolicyContent() {
  return (
    <>
      <DocP>
        This Privacy Policy describes how Autranslate (“we”, “us”, “our”) collects, uses, and
        protects information when you install and use our Shopify embedded application (the “App”).
      </DocP>

      <DocHr />

      <DocH2>1. Scope</DocH2>
      <DocP>
        This policy applies to merchants who install the App on their Shopify stores. The App is
        designed for <strong>store and product content localization</strong>, not for collecting
        end-customer checkout data as a primary function.
      </DocP>

      <DocHr />

      <DocH2>2. Information we collect</DocH2>

      <DocH3>2.1 Information from Shopify (with your permission)</DocH3>
      <DocP>
        When you install the App, we access data through Shopify APIs according to the scopes you
        approve:
      </DocP>
      <DocTable>
        <thead>
          <tr>
            <th>Scope</th>
            <th>Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <DocCode>read_products</DocCode>
            </td>
            <td>Read product titles, descriptions, and translatable content to generate translations</td>
          </tr>
          <tr>
            <td>
              <DocCode>read_locales</DocCode>
            </td>
            <td>Read enabled store languages/locales for translation targets</td>
          </tr>
          <tr>
            <td>
              <DocCode>write_translations</DocCode>
            </td>
            <td>Publish approved translations to your Shopify store</td>
          </tr>
        </tbody>
      </DocTable>
      <DocP>
        We also receive standard OAuth/session data required to operate the App (for example: shop
        domain, access tokens stored securely, and session metadata).
      </DocP>

      <DocH3>2.2 Information you provide in the App</DocH3>
      <DocUl>
        <li>Language pair and translation settings (source/target locale, tone, brand dictionary)</li>
        <li>Translation drafts, review edits, and publishing choices</li>
        <li>Usage/credit balance related to your subscription plan</li>
      </DocUl>

      <DocH3>2.3 Information processed for AI translation</DocH3>
      <DocP>
        To generate translations, product content you select (such as title, description HTML, SEO
        fields) may be sent to our <strong>AI service provider</strong> (Anthropic) for processing. We
        use this only to provide translation functionality you request.
      </DocP>

      <DocH3>2.4 Billing</DocH3>
      <DocP>
        Subscription and usage charges are handled through <strong>Shopify’s billing system</strong>.
        We do not collect or store your credit card details. Shopify processes payments according to
        its own privacy policy.
      </DocP>

      <DocH3>2.5 Information we do not intentionally collect</DocH3>
      <DocP>
        The App is not designed to collect personal data about your store’s customers (buyers) as part
        of normal operation. If Shopify sends mandatory compliance webhooks (see Section 6), we
        respond as required by law and platform policy.
      </DocP>

      <DocHr />

      <DocH2>3. How we use information</DocH2>
      <DocP>We use collected information to:</DocP>
      <DocUl>
        <li>Authenticate your store and operate the App</li>
        <li>Generate, review, and publish product translations you request</li>
        <li>Enforce plan limits (monthly credits and usage-based overage billing)</li>
        <li>Maintain security, prevent abuse, and improve reliability</li>
        <li>Comply with legal obligations and Shopify platform requirements</li>
      </DocUl>
      <DocP>
        We do <strong>not</strong> sell your data to third parties.
      </DocP>

      <DocHr />

      <DocH2>4. Data storage and retention</DocH2>
      <DocUl>
        <li>
          Session and app data are stored on servers used to host the App (currently cloud hosting in
          the United States region, subject to change with notice in an updated policy).
        </li>
        <li>
          Translation records and shop settings are retained while the App is installed and you use
          the service.
        </li>
        <li>
          When you uninstall the App, Shopify session data is removed as part of our uninstall
          handling.
        </li>
        <li>
          After uninstall, Shopify may send a <DocCode>shop/redact</DocCode> compliance webhook; we
          delete remaining shop-associated app data in response to that request, in line with
          Shopify’s mandatory compliance requirements.
        </li>
      </DocUl>

      <DocHr />

      <DocH2>5. Sharing with third parties</DocH2>
      <DocP>We share data only as needed to operate the App:</DocP>
      <DocTable>
        <thead>
          <tr>
            <th>Recipient</th>
            <th>Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Shopify</td>
            <td>App platform, APIs, authentication, billing</td>
          </tr>
          <tr>
            <td>Anthropic</td>
            <td>AI translation processing for content you submit</td>
          </tr>
          <tr>
            <td>Hosting provider</td>
            <td>Application infrastructure</td>
          </tr>
        </tbody>
      </DocTable>
      <DocP>
        Each provider is used under contractual and technical safeguards appropriate to their role.
      </DocP>

      <DocHr />

      <DocH2>6. Mandatory compliance webhooks (GDPR / privacy laws)</DocH2>
      <DocP>For App Store distribution, we implement Shopify mandatory compliance webhooks:</DocP>
      <DocUl>
        <li>
          <DocCode>customers/data_request</DocCode>
        </li>
        <li>
          <DocCode>customers/redact</DocCode>
        </li>
        <li>
          <DocCode>shop/redact</DocCode>
        </li>
      </DocUl>
      <DocP>
        <strong>Our typical data footprint:</strong> the App focuses on merchant product content and
        shop configuration. If we do not hold customer personal data for a given request, we
        acknowledge the webhook and take no further action beyond what is required. For{" "}
        <DocCode>shop/redact</DocCode>, we delete shop-associated app data as described above.
      </DocP>

      <DocHr />

      <DocH2>7. Security</DocH2>
      <DocP>
        We use industry-standard measures including HTTPS/TLS, authenticated API access, and access
        controls on application infrastructure. No method of transmission or storage is 100%
        secure; we cannot guarantee absolute security.
      </DocP>

      <DocHr />

      <DocH2>8. Your rights and choices</DocH2>
      <DocP>
        Depending on your location, you may have rights to access, correct, or delete personal data.
        Merchants can:
      </DocP>
      <DocUl>
        <li>Uninstall the App to stop new data processing</li>
        <li>
          Contact us at <strong>yosui262@gmail.com</strong> for privacy-related requests
        </li>
      </DocUl>
      <DocP>
        For buyer/customer data requests handled by Shopify, merchants should use Shopify admin
        tools; we respond to mandatory app webhooks as described in Section 6.
      </DocP>

      <DocHr />

      <DocH2>9. International transfers</DocH2>
      <DocP>
        If you operate outside the country where our servers are located, your data may be processed
        in other jurisdictions with different data protection laws.
      </DocP>

      <DocHr />

      <DocH2>10. Children</DocH2>
      <DocP>
        The App is intended for businesses (merchants) and is not directed to children.
      </DocP>

      <DocHr />

      <DocH2>11. Changes to this policy</DocH2>
      <DocP>
        We may update this Privacy Policy from time to time. We will post the updated version with a
        new “Last updated” date. Continued use of the App after changes constitutes acceptance of the
        updated policy.
      </DocP>

      <DocHr />

      <DocH2>12. Contact</DocH2>
      <DocP>Questions about this Privacy Policy:</DocP>
      <DocP>
        <strong>Email:</strong>{" "}
        <DocLink href="mailto:yosui262@gmail.com">yosui262@gmail.com</DocLink>
      </DocP>

      <DocHr />

      <DocEm>
        This document is provided for App Store listing and merchant transparency. It does not
        constitute legal advice.
      </DocEm>
    </>
  );
}
